import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  Calendar,
  FileText,
  BookOpen,
  Newspaper,
} from 'lucide-react';
import {
  economicCalendarAPI,
  fredCalendarAPI,
  newsAPI,
} from '../services/api';
import type { NewsArticleSummary } from '../services/api';

// ============================================================================
// FRED RELEASE CALENDAR CARD
// ============================================================================

interface FredCalendarRelease {
  release_id: number;
  release_name: string;
  release_date: string;
  series_count: number;
}

interface FredCalendarWeekData {
  week_start: string;
  week_end: string;
  total_releases: number;
  releases_by_date: Record<string, FredCalendarRelease[]>;
  releases: FredCalendarRelease[];
}

function FREDCalendarCard() {
  const { data: weekData, isLoading: loading } = useQuery({
    queryKey: ['fred-calendar-week'],
    queryFn: async () => {
      const res = await fredCalendarAPI.getWeek<FredCalendarWeekData>();
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const releases: FredCalendarRelease[] = weekData?.releases || [];
  const today = new Date().toISOString().split('T')[0];

  const byDate: Record<string, FredCalendarRelease[]> = {};
  releases.forEach((r: FredCalendarRelease) => {
    if (r.release_date >= today) {
      if (!byDate[r.release_date]) byDate[r.release_date] = [];
      byDate[r.release_date].push(r);
    }
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isToday = (dateStr: string) => dateStr === today;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm h-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-500" />
          <h3 className="font-semibold text-gray-900 text-sm">FRED Release Calendar</h3>
        </div>
        <Link to="/research/fred-calendar" className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
          View All →
        </Link>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-14 bg-gray-100 rounded-lg"></div>
            <div className="h-14 bg-gray-100 rounded-lg"></div>
          </div>
        ) : Object.keys(byDate).length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No upcoming releases this week</p>
        ) : (
          <div className="space-y-2 flex-1">
            {Object.entries(byDate).slice(0, 4).map(([date, items]) => (
              <div
                key={date}
                className={`p-2.5 rounded-lg border ${
                  isToday(date)
                    ? 'bg-indigo-50 border-indigo-300'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className={`text-xs font-bold mb-1.5 flex items-center gap-2 ${isToday(date) ? 'text-indigo-700' : 'text-gray-600'}`}>
                  {formatDate(date)}
                  {isToday(date) && (
                    <span className="px-1.5 py-0.5 bg-indigo-500 text-white rounded text-[10px] font-semibold">TODAY</span>
                  )}
                </div>
                <div className="space-y-1">
                  {items.slice(0, 3).map((r, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <span className="text-xs text-gray-800 truncate mr-2">{r.release_name}</span>
                      <span className="text-[10px] text-gray-500 bg-white px-1 py-0.5 rounded border border-gray-200">{r.series_count}</span>
                    </div>
                  ))}
                  {items.length > 3 && (
                    <div className="text-[10px] text-gray-500">+{items.length - 3} more</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-auto pt-2 border-t border-gray-100 text-center">
          <span className="text-xs font-medium text-gray-500">
            {Object.values(byDate).flat().length} upcoming releases
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ECONOMIC CALENDAR CARD
// ============================================================================

interface EconomicCalendarEvent {
  date: string;
  time: string;
  datetime: string;
  name: string;
  country: string;
  source: string;
  impact: string | null;
  importance: number;
  previous: number | null;
  estimate: number | null;
  actual: number | null;
}

interface EconomicCalendarData {
  as_of: string;
  count: number;
  data_source?: string;
  events: EconomicCalendarEvent[];
}

function CalendarCard() {
  const [activeTab, setActiveTab] = useState<'today' | 'upcoming'>('today');

  const { data: allEvents = [], isLoading: loading } = useQuery({
    queryKey: ['economic-calendar-upcoming'],
    queryFn: async () => {
      const res = await economicCalendarAPI.getUpcoming<EconomicCalendarData>({
        days: 14,
        country: 'US',
        limit: 20
      });
      return res.data.events || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const today = new Date().toDateString();
  const todayEvents = allEvents.filter(e => {
    if (!e.datetime) return false;
    return new Date(e.datetime).toDateString() === today;
  });
  const upcomingEvents = allEvents.filter(e => {
    if (!e.datetime) return false;
    return new Date(e.datetime).toDateString() !== today;
  });

  const displayEvents = activeTab === 'today' ? todayEvents : upcomingEvents;

  const getImpactColor = (impact: string | null) => {
    switch (impact) {
      case 'High': return 'bg-red-500';
      case 'Medium': return 'bg-amber-500';
      case 'Low': return 'bg-gray-400';
      default: return 'bg-gray-300';
    }
  };

  const formatValue = (val: number | null) => {
    if (val === null) return '--';
    if (Math.abs(val) >= 1000) return `${(val / 1000).toFixed(1)}K`;
    if (Math.abs(val) >= 1) return val.toFixed(1);
    return val.toFixed(2);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <h3 className="font-semibold text-gray-900 text-sm">Economic Calendar</h3>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('today')}
            className={`px-2 py-0.5 text-xs rounded ${
              activeTab === 'today'
                ? 'bg-indigo-100 text-indigo-700 font-medium'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            Today ({todayEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-2 py-0.5 text-xs rounded ${
              activeTab === 'upcoming'
                ? 'bg-indigo-100 text-indigo-700 font-medium'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            Upcoming ({upcomingEvents.length})
          </button>
        </div>
      </div>
      <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">Loading...</div>
        ) : displayEvents.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400">
            {activeTab === 'today' ? 'No events today' : 'No upcoming events'}
          </div>
        ) : (
          displayEvents.map((e, i) => (
            <div key={i} className="px-3 py-2 hover:bg-gray-50">
              <div className="flex items-start gap-2">
                <div className={`w-1 h-full min-h-[36px] rounded-full ${getImpactColor(e.impact)}`} />
                <div className="w-14 flex-shrink-0">
                  <div className="text-xs font-medium text-gray-900">{e.time}</div>
                  {activeTab === 'upcoming' && (
                    <div className="text-[10px] text-gray-400">{e.date}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 truncate">{e.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {e.actual !== null ? (
                      <>
                        <span className="text-[10px] text-gray-500">Act: <span className="font-medium text-gray-700">{formatValue(e.actual)}</span></span>
                        <span className="text-[10px] text-gray-400">Est: {formatValue(e.estimate)}</span>
                        <span className="text-[10px] text-gray-400">Prev: {formatValue(e.previous)}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-[10px] text-gray-500">Est: <span className="font-medium text-gray-700">{formatValue(e.estimate)}</span></span>
                        <span className="text-[10px] text-gray-400">Prev: {formatValue(e.previous)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <Link to="/research/calendar" className="block px-4 py-2 text-center text-xs text-indigo-600 hover:text-indigo-700 font-medium border-t border-gray-100">
        View Full Calendar →
      </Link>
    </div>
  );
}

// ============================================================================
// ECONOMIC NEWS SECTION
// ============================================================================

function EconomicNewsSection() {
  const [page, setPage] = useState(0);
  const limit = 12;

  const { data, isLoading } = useQuery({
    queryKey: ['economic-news', page],
    queryFn: async () => {
      const response = await newsAPI.getSectionArticles('economic', {
        limit,
        offset: page * limit,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const articles = data?.articles || [];
  const totalCount = data?.total_count || 0;
  const totalPages = Math.ceil(totalCount / limit);

  const getSentimentColor = (sentiment: string | null) => {
    switch (sentiment) {
      case 'positive': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'negative': return 'bg-red-50 text-red-700 border-red-200';
      case 'neutral': return 'bg-gray-50 text-gray-600 border-gray-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const leftColumn = articles.slice(0, 6);
  const rightColumn = articles.slice(6, 12);

  return (
    <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-5 border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-amber-100 border border-amber-200">
            <Newspaper className="w-4 h-4 text-amber-600" />
          </div>
          <h3 className="font-semibold text-gray-900 text-sm tracking-wide">Economic News</h3>
          {totalCount > 0 && (
            <span className="text-xs text-gray-400">{totalCount} reports</span>
          )}
        </div>
        <Link
          to="/news/section/economic"
          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
        >
          View All <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            Loading economic news...
          </div>
        </div>
      ) : articles.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 text-sm">No economic reports available</div>
        </div>
      ) : (
        <>
          {/* Two-column article list */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2.5">
            {[leftColumn, rightColumn].map((column, colIdx) => (
              <div key={colIdx} className="space-y-2.5">
                {column.map((article: NewsArticleSummary) => (
                  <a
                    key={`${article.source}-${article.id}`}
                    href={newsAPI.getArticleHtmlUrl(article.source, article.id)}
                    className="group relative flex items-start gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-amber-200 hover:shadow-md hover:shadow-amber-100/50 transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <div className="absolute left-0 top-3 bottom-3 w-0.5 bg-gradient-to-b from-amber-500 to-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                    <FileText className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0 group-hover:text-amber-600 transition-colors" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <span className="text-sm text-gray-800 group-hover:text-gray-900 transition-colors line-clamp-2 font-medium leading-snug">
                          {article.title}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {article.indicator_type && (
                          <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-100 font-medium">
                            {article.indicator_type}
                          </span>
                        )}
                        {article.key_stat && (
                          <span className="text-[10px] text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100 font-medium">
                            {article.key_stat_label ? `${article.key_stat_label}: ${article.key_stat}` : article.key_stat}
                          </span>
                        )}
                        {article.sentiment && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-md border font-medium ${getSentimentColor(article.sentiment)}`}>
                            {article.sentiment}
                          </span>
                        )}
                        {article.time_ago && (
                          <span className="text-[10px] text-gray-400 ml-auto">
                            {article.time_ago}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-0.5" />
                  </a>
                ))}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-5 pt-4 border-t border-gray-200">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Prev
              </button>
              <span className="text-xs text-gray-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// MARKET IMPACT INSIGHTS (static articles from manifest)
// ============================================================================

interface MarketInsightArticle {
  id: string;
  title: string;
  tags: string[];
  path: string;
  category?: string;
  date?: string;
  isNew?: boolean;
  order?: number;
}

interface ManifestData {
  categories: { id: string; name: string; description: string }[];
  articles: MarketInsightArticle[];
}

const INSIGHTS_MANIFEST_VERSION = '43';

function MarketImpactInsights() {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [articles, setArticles] = useState<MarketInsightArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const articlesPerPage = 10;

  useEffect(() => {
    fetch(`/insights/manifest.json?v=${INSIGHTS_MANIFEST_VERSION}`)
      .then(res => res.json())
      .then((data: ManifestData) => {
        const sorted = [...data.articles].sort((a, b) => {
          const dateA = new Date(a.date || '1970-01-01').getTime();
          const dateB = new Date(b.date || '1970-01-01').getTime();
          if (dateB !== dateA) return dateB - dateA;
          return (b.order ?? 0) - (a.order ?? 0);
        });
        setArticles(sorted);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Failed to load insights manifest:', err);
        setIsLoading(false);
      });
  }, []);

  const filteredArticles = selectedCategory === 'all'
    ? articles
    : articles.filter(a => a.category === selectedCategory);

  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const currentArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);

  const leftColumn = currentArticles.slice(0, 5);
  const rightColumn = currentArticles.slice(5, 10);

  const ArticleItem = ({ article }: { article: MarketInsightArticle }) => (
    <a
      href={article.path}
      className="group relative flex items-start gap-3 p-3 rounded-xl bg-white border border-gray-100 hover:border-indigo-200 hover:shadow-md hover:shadow-indigo-100/50 transition-all duration-200 hover:-translate-y-0.5"
    >
      <div className="absolute left-0 top-3 bottom-3 w-0.5 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
      <FileText className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0 group-hover:text-indigo-600 transition-colors" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <span className="text-sm text-gray-800 group-hover:text-gray-900 transition-colors line-clamp-2 font-medium leading-snug">
            {article.title}
          </span>
          {article.isNew && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-md flex-shrink-0 shadow-sm shadow-emerald-500/30">
              NEW
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {article.tags.slice(0, 5).map((tag, i) => (
            <span key={i} className="text-[10px] text-gray-500 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100 group-hover:border-gray-200 group-hover:bg-gray-100 transition-colors">
              {tag}
            </span>
          ))}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-0.5" />
    </a>
  );

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-5 border border-gray-200">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="p-1.5 rounded-lg bg-indigo-100 border border-indigo-200">
            <BookOpen className="w-4 h-4 text-indigo-600" />
          </div>
          <h3 className="font-semibold text-gray-900 text-sm tracking-wide">Market Impact Insights</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            Loading insights...
          </div>
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-5 border border-gray-200">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="p-1.5 rounded-lg bg-indigo-100 border border-indigo-200">
            <BookOpen className="w-4 h-4 text-indigo-600" />
          </div>
          <h3 className="font-semibold text-gray-900 text-sm tracking-wide">Market Impact Insights</h3>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 text-sm">No articles available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-5 border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-indigo-100 border border-indigo-200">
            <BookOpen className="w-4 h-4 text-indigo-600" />
          </div>
          <h3 className="font-semibold text-gray-900 text-sm tracking-wide">Market Impact Insights</h3>
        </div>
        <span className="text-xs text-gray-500 bg-white px-2.5 py-1 rounded-md border border-gray-200">
          Page {currentPage} of {totalPages}
        </span>
      </div>

      {/* Category filter tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
        {[
          { key: 'all', label: 'All' },
          { key: 'bls', label: 'Labor & Prices' },
          { key: 'bea', label: 'Economic Accounts' },
          { key: 'fred', label: 'Fed & Rates' },
          { key: 'market', label: 'Market' },
        ].map(cat => (
          <button
            key={cat.key}
            onClick={() => { setSelectedCategory(cat.key); setCurrentPage(1); }}
            className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-all ${
              selectedCategory === cat.key
                ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-200'
                : 'bg-white text-gray-500 border border-gray-200 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Two-column article list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2.5">
        <div className="space-y-2.5">
          {leftColumn.map((article) => (
            <ArticleItem key={article.id} article={article} />
          ))}
        </div>
        <div className="space-y-2.5">
          {rightColumn.map((article) => (
            <ArticleItem key={article.id} article={article} />
          ))}
        </div>
      </div>

      {/* Pagination with sliding window */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-5 pt-4 border-t border-gray-200">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </button>
          <div className="flex gap-1.5">
            {(() => {
              const pages: (number | string)[] = [];
              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                pages.push(1);
                if (currentPage > 3) pages.push('...');
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                if (currentPage < totalPages - 2) pages.push('...');
                pages.push(totalPages);
              }
              return pages.map((p, i) =>
                typeof p === 'string' ? (
                  <span key={`ellipsis-${i}`} className="px-1 text-xs text-gray-400">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`min-w-[28px] h-7 text-xs rounded-md transition-all ${
                      p === currentPage
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-gray-500 hover:bg-white hover:text-gray-900'
                    }`}
                  >
                    {p}
                  </button>
                )
              );
            })()}
          </div>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Research(): React.ReactElement {
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Explorer Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3">
          <div className="flex items-center gap-1 overflow-x-auto">
            {[
              { label: 'Snapshot', path: '/research/economic-snapshot' },
              { label: 'FRED', path: '/research/fred-calendar' },
              { label: 'Labor', path: '/research/bls/ln' },
              { label: 'Inflation', path: '/research/bls/cu' },
              { label: 'GDP', path: '/research/bea' },
              { label: 'Treasury', path: '/research/treasury' },
              { label: 'Fed', path: '/research/fed-funds' },
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex-shrink-0 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50 transition-all shadow-sm"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4">
        {/* Market Impact Insights */}
        <div className="mb-4">
          <MarketImpactInsights />
        </div>

        {/* Economic News - full width */}
        <EconomicNewsSection />

        {/* Calendars - side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <CalendarCard />
          <FREDCalendarCard />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-6">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3">
          <p className="text-[10px] text-gray-400">
            Data: BLS, U.S. Treasury, BEA, Federal Reserve. For informational purposes only.
          </p>
        </div>
      </footer>
    </div>
  );
}
